/**
 * @file Checks for valid permissions in album folder.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect, withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import get from 'lodash.get';
import { connect } from 'react-redux';

import { ALBUM_IDENTITY_ACCESS } from '../../../constants/identity';
import { AlbumFolderIdentityAccess } from '../../../graphql/queries/album-folder';

/**
 * Higher order component for the accesss control functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @param {Object} opts The options object.
 * @param {Function} opts.getRedirectUrl Method to get URL to redirect to if access is not granted.
 * @param {boolean} opts.requiresAuth Whether or not the user is required to be authed.
 * @returns {React.Component} The composed component.
 */
const WithAlbumFolderAccessControlHOC = (WrappedComponent, opts = {}) =>
  class AlbumFolderAccessControl extends Component {
    static propTypes = {
      authenticated: PropTypes.bool.isRequired,
      albumFolder: PropTypes.shape({
        id: PropTypes.string.isRequired,
        album_identity_access: PropTypes.oneOf(
          Object.values(ALBUM_IDENTITY_ACCESS),
        ).isRequired,
      }),
      loading: PropTypes.bool.isRequired,
    };

    render() {
      const { albumFolder, loading, authenticated } = this.props;
      const { requiresAuth, getRedirectUrl } = opts;

      if (!authenticated && !requiresAuth) {
        return <WrappedComponent {...this.props} />;
      }

      if (!loading && !albumFolder) {
        return <Redirect to="/not-found" />;
      }

      return !loading &&
        get(albumFolder, 'album_identity_access') ===
          ALBUM_IDENTITY_ACCESS.NONE ? (
        <Redirect
          to={
            getRedirectUrl
              ? getRedirectUrl(this.props)
              : `/discover/album-folder/${albumFolder.id}/request-access`
          }
        />
      ) : (
        <WrappedComponent {...this.props} />
      );
    }
  };

/**
 * Higher order container for the album folder access control functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const AlbumFolderAccessControl = WrappedComponent => {
  const WithAlbumFolderAccessControl =
    WithAlbumFolderAccessControlHOC(WrappedComponent);

  const withAlbumFolderData = graphql(AlbumFolderIdentityAccess, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumFolderId,
      },
      // Important so that the query gets refetched after the user logs in.
      fetchPolicy: 'network-only',
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      albumFolder,
      loading,
    }),
    skip: ({ authenticated }) => !authenticated,
  });

  const mapStateToProps = ({ auth }) => ({
    authenticated: auth.authenticated,
  });

  return compose(
    withRouter,
    connect(mapStateToProps),
    withAlbumFolderData,
  )(WithAlbumFolderAccessControl);
};

export { WithAlbumFolderAccessControlHOC };
export default AlbumFolderAccessControl;
